import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, ImageIcon, Upload, X } from 'lucide-react';
import type { MediaRef } from '@geoic/shared';
import { cn } from '@/lib/cn.js';
import { fileSize } from '@/lib/format.js';
import { queryKeys } from '@/lib/query-keys.js';
import { Button } from '@/components/ui/Button.js';
import { Modal } from '@/components/ui/Modal.js';
import { EmptyState } from '@/components/ui/DataTable.js';
import { useToast } from '@/components/ui/Toast.js';
import { mediaApi, type MediaItem } from '@/features/media/media-api.js';

export type MediaPickerProps = {
  label: string;
  value: MediaRef | null;
  onChange: (value: MediaRef | null) => void;
  /** `file` — PDF va shu kabilar (hujjatlar bo'limi). */
  kind?: 'image' | 'file';
  hint?: string;
};

/**
 * Media tanlash maydoni.
 *
 * Tanlangan fayl `mediaId` bo'yicha saqlanadi, URL emas: domen o'zgarganda
 * yoki S3 ga ko'chirilganda bazadagi ma'lumot o'zgarmasligi kerak
 * (docs/02-DATABASE.md §4.2).
 */
export function MediaPicker({ label, value, onChange, kind = 'image', hint }: MediaPickerProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-1.5">
      <span className="block text-sm font-medium text-n-800">{label}</span>

      {value ? (
        <SelectedPreview
          mediaId={value.mediaId}
          onClear={() => onChange(null)}
          onReplace={() => setOpen(true)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex w-full items-center justify-center gap-2 border-2 border-dashed border-n-200 px-4 py-6 text-sm text-n-600 hover:border-steel-500 hover:bg-brand-50"
        >
          {kind === 'image' ? (
            <ImageIcon className="size-4" aria-hidden />
          ) : (
            <FileText className="size-4" aria-hidden />
          )}
          {kind === 'image' ? 'Rasm tanlash' : 'Fayl tanlash'}
        </button>
      )}

      {hint && <p className="text-sm text-n-600">{hint}</p>}

      <MediaPickerModal
        open={open}
        kind={kind}
        onClose={() => setOpen(false)}
        onSelect={(item) => {
          onChange({ mediaId: item._id, alt: null });
          setOpen(false);
        }}
      />
    </div>
  );
}

/**
 * Tanlangan faylni ko'rsatadi.
 *
 * Faylning o'zi `mediaId` bo'yicha alohida so'raladi: forma ichida faqat
 * identifikator saqlanadi, nom va rasm esa har doim eng yangi holatda
 * bo'lishi kerak (media kutubxonada alt matni o'zgargan bo'lishi mumkin).
 */
function SelectedPreview({
  mediaId,
  onClear,
  onReplace,
}: {
  mediaId: string;
  onClear: () => void;
  onReplace: () => void;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['media', 'one', mediaId],
    queryFn: () => mediaApi.getOne(mediaId),
  });

  const item = data?.data;

  return (
    <div className="flex items-center gap-3 border border-n-200 bg-white p-2">
      <div className="size-14 shrink-0 overflow-hidden bg-n-100">
        {item ? (
          <MediaThumb item={item} className="size-14" />
        ) : (
          <div className="flex size-14 items-center justify-center">
            <ImageIcon className="size-5 text-n-400" aria-hidden />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        {isLoading && <p className="text-sm text-n-600">Yuklanmoqda…</p>}
        {isError && (
          <p className="text-sm text-error-600">Fayl topilmadi — u o‘chirilgan bo‘lishi mumkin</p>
        )}
        {item && (
          <>
            <p className="truncate text-sm text-n-800">{item.originalName}</p>
            <p className="text-xs text-n-400">{fileSize(item.size)}</p>
          </>
        )}
      </div>

      <Button variant="ghost" size="sm" onClick={onReplace}>
        Almashtirish
      </Button>
      <Button variant="ghost" size="sm" onClick={onClear} aria-label="Olib tashlash">
        <X className="size-4" aria-hidden />
      </Button>
    </div>
  );
}

export type MediaPickerModalProps = {
  open: boolean;
  kind: 'image' | 'file';
  onClose: () => void;
  onSelect: (item: MediaItem) => void;
};

/** Kutubxonadan tanlash yoki darhol yuklash. */
export function MediaPickerModal({ open, kind, onClose, onSelect }: MediaPickerModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');

  const params = { page: 1, limit: 24, kind, search: search || undefined };
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.media.list(params),
    queryFn: () => mediaApi.list(params),
    enabled: open,
  });

  const upload = useMutation({
    mutationFn: (files: File[]) => mediaApi.upload(files, null),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.media.all });
      const first = result.data[0];
      if (first) onSelect(first);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Modal open={open} onClose={onClose} title="Media kutubxona" size="xl">
      <div className="mb-4 flex gap-2">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Fayl nomi bo‘yicha qidirish"
          className="h-10 flex-1 border border-n-200 px-3 text-sm"
        />
        <Button
          icon={<Upload className="size-4" aria-hidden />}
          loading={upload.isPending}
          onClick={() => inputRef.current?.click()}
        >
          Yuklash
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          accept={kind === 'image' ? 'image/jpeg,image/png,image/webp' : 'application/pdf'}
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            if (files.length > 0) upload.mutate(files);
            event.target.value = '';
          }}
        />
      </div>

      {isLoading && <p className="py-8 text-center text-sm text-n-600">Yuklanmoqda…</p>}

      {!isLoading && (data?.data.length ?? 0) === 0 && (
        <EmptyState
          title="Kutubxona bo‘sh"
          hint="«Yuklash» tugmasi orqali birinchi faylni qo‘shing."
        />
      )}

      <div className="grid max-h-[60vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-4 lg:grid-cols-6">
        {data?.data.map((item) => (
          <button
            key={item._id}
            type="button"
            onClick={() => onSelect(item)}
            className="group overflow-hidden border border-n-200 text-left hover:border-steel-500"
          >
            <MediaThumb item={item} />
            <div className="p-2">
              <p className="truncate text-xs text-n-800">{item.originalName}</p>
              <p className="text-[11px] text-n-400">{fileSize(item.size)}</p>
            </div>
          </button>
        ))}
      </div>
    </Modal>
  );
}

export function MediaThumb({ item, className }: { item: MediaItem; className?: string }) {
  const src = item.urls.thumb ?? item.urls.square ?? item.urls.original;
  const isImage = item.mimeType.startsWith('image/');

  if (!isImage) {
    return (
      <div className={cn('flex aspect-square items-center justify-center bg-n-100', className)}>
        <FileText className="size-6 text-n-400" aria-hidden />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={item.alt.uz || item.originalName}
      loading="lazy"
      className={cn('aspect-square w-full bg-n-100 object-cover', className)}
    />
  );
}
